import * as AvatarPrimivite from '@radix-ui/react-avatar';
import classNames from 'clsx';
import * as React from 'react';

interface AvatarProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimivite.Root> {
  name?: string;
  src?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimivite.Root>,
  AvatarProps
>(({ className, name, src, size = 'md', ...props }, ref) => {
  // Get initials from name
  const initials = React.useMemo(() => {
    if (!name) return '';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, [name]);

  const sizeClasses = {
    xs: 'h-6 w-6 text-xs',
    sm: 'h-8 w-8 text-sm',
    md: 'h-10 w-10 text-base',
    lg: 'h-12 w-12 text-lg',
  };

  return (
    <AvatarPrimivite.Root
      ref={ref}
      className={classNames(
        'relative flex shrink-0 overflow-hidden rounded-full',
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      {src && <AvatarImage src={src} alt={name || 'Avatar'} />}
      <AvatarFallback>{initials}</AvatarFallback>
    </AvatarPrimivite.Root>
  );
});

Avatar.displayName = 'Avatar';

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimivite.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimivite.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimivite.Image
    ref={ref}
    className={classNames('aspect-square h-full w-full', className)}
    {...props}
  />
));

AvatarImage.displayName = AvatarPrimivite.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimivite.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimivite.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimivite.Fallback
    ref={ref}
    className={classNames(
      `flex h-full w-full items-center justify-center rounded-full bg-black dark:bg-[#596487] font-semibold uppercase text-primary-foreground`,
      className,
    )}
    {...props}
  />
));

AvatarFallback.displayName = AvatarPrimivite.Fallback.displayName;

export { Avatar, AvatarFallback, AvatarImage };
export default Avatar;
